import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useAuth } from "../auth/AuthProvider";
import { useMembersIndex } from "../team/useMembersIndex";
import {
  useLeadCampaignsQuery,
  useLeadCitiesQuery,
  useLeadStatesQuery,
} from "./api";
import type { UseLeadFiltersResult } from "./useLeadFilters";
import { STATUS_OPTIONS, SOURCE_OPTIONS } from "./leadFilterLabels";

interface Props {
  workspaceSlug: string | null;
  filters: UseLeadFiltersResult["filters"];
  activeCount: number;
  searchInput: string;
  onSearchInputChange: (v: string) => void;
  setStatus: UseLeadFiltersResult["setStatus"];
  setSource: UseLeadFiltersResult["setSource"];
  setAssignee: UseLeadFiltersResult["setAssignee"];
  setCampaign: UseLeadFiltersResult["setCampaign"];
  setCity: UseLeadFiltersResult["setCity"];
  setState: UseLeadFiltersResult["setState"];
  setFrom: UseLeadFiltersResult["setFrom"];
  setTo: UseLeadFiltersResult["setTo"];
  setFollowUp: UseLeadFiltersResult["setFollowUp"];
  setSort: UseLeadFiltersResult["setSort"];
  clear: () => void;
}

const FOLLOW_UP_OPTIONS = [
  { value: "due", label: "Due" },
  { value: "scheduled", label: "Scheduled" },
];

const SORT_OPTIONS = [
  { value: "follow_up_asc", label: "Follow-up ↑" },
  { value: "follow_up_desc", label: "Follow-up ↓" },
  { value: "assigned_desc", label: "Assigned ↓" },
  { value: "assigned_asc", label: "Assigned ↑" },
];

// Human-readable sort summary shown below the filter chips. Mirrors the
// sort→sortBy/sortDir mapping in LeadsPage; the empty value resolves to the
// applied default, which differs for sales reps (see `isSales`).
const SORT_SUMMARY_LABELS: Record<string, string> = {
  follow_up_asc: "Follow-up date (earliest first)",
  follow_up_desc: "Follow-up date (latest first)",
  assigned_desc: "Most recently assigned",
  assigned_asc: "Earliest assigned",
};

function sortSummaryLabel(sort: string, isSales: boolean): string {
  const explicit = SORT_SUMMARY_LABELS[sort];
  if (explicit) return explicit;
  // No explicit sort picked — surface whichever default actually applies.
  return isSales
    ? "Most recently assigned (default)"
    : "Newest first (default)";
}

export function LeadFilterBar(props: Props) {
  const {
    workspaceSlug,
    filters,
    activeCount,
    searchInput,
    onSearchInputChange,
    setStatus,
    setSource,
    setAssignee,
    setCampaign,
    setCity,
    setState,
    setFrom,
    setTo,
    setFollowUp,
    setSort,
    clear,
  } = props;

  const [drawerOpen, setDrawerOpen] = useState(false);

  // Sales reps get an implicit `assigned_at desc` default (see LeadsPage); the
  // summary line makes that—and any explicit pick—visible below the chips.
  const { user } = useAuth();
  const isSales = !!user && !user.isSuperadmin && user.role === "sales";

  // Lock body scroll while drawer is open + close on ESC.
  useEffect(() => {
    if (!drawerOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  const membersIndex = useMembersIndex();
  const memberOptions = useMemo(() => {
    return membersIndex.list
      .filter((m) => !m.deletedAt)
      .map((m) => ({ value: m.id, label: m.name || m.email }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [membersIndex.list]);

  const { data: campaignsData } = useLeadCampaignsQuery(workspaceSlug);
  const campaignOptions = useMemo(() => {
    const list = campaignsData?.campaigns ?? [];
    return list.map((c) => ({ value: c, label: c }));
  }, [campaignsData]);

  const { data: citiesData } = useLeadCitiesQuery(workspaceSlug);
  const cityOptions = useMemo(() => {
    const list = citiesData?.cities ?? [];
    return list.map((c) => ({ value: c, label: c }));
  }, [citiesData]);

  const { data: statesData } = useLeadStatesQuery(workspaceSlug);
  const stateOptions = useMemo(() => {
    const list = statesData?.states ?? [];
    return list.map((s) => ({ value: s, label: s }));
  }, [statesData]);

  const chips: Array<{
    key: string;
    label: string;
    value: string;
    onRemove: () => void;
  }> = [];
  if (filters.status) {
    const opt = STATUS_OPTIONS.find((o) => o.value === filters.status);
    chips.push({
      key: "status",
      label: "Status",
      value: opt?.label ?? filters.status,
      onRemove: () => setStatus(""),
    });
  }
  if (filters.source) {
    const opt = SOURCE_OPTIONS.find((o) => o.value === filters.source);
    chips.push({
      key: "source",
      label: "Source",
      value: opt?.label ?? filters.source,
      onRemove: () => setSource(""),
    });
  }
  if (filters.assignee) {
    chips.push({
      key: "assignee",
      label: "Assignee",
      value: membersIndex.resolveName(filters.assignee),
      onRemove: () => setAssignee(""),
    });
  }
  if (filters.campaign) {
    chips.push({
      key: "campaign",
      label: "Campaign",
      value: filters.campaign,
      onRemove: () => setCampaign(""),
    });
  }
  if (filters.city.length) {
    chips.push({
      key: "city",
      label: filters.city.length > 1 ? "Cities" : "City",
      value: filters.city.join(", "),
      onRemove: () => setCity([]),
    });
  }
  if (filters.state.length) {
    chips.push({
      key: "state",
      label: filters.state.length > 1 ? "States" : "State",
      value: filters.state.join(", "),
      onRemove: () => setState([]),
    });
  }
  if (filters.from || filters.to) {
    chips.push({
      key: "date",
      label: "Date",
      value: `${filters.from || "…"} → ${filters.to || "…"}`,
      onRemove: () => {
        setFrom("");
        setTo("");
      },
    });
  }
  if (filters.followUp) {
    const opt = FOLLOW_UP_OPTIONS.find((o) => o.value === filters.followUp);
    chips.push({
      key: "followUp",
      label: "Follow-up",
      value: opt?.label ?? filters.followUp,
      onRemove: () => setFollowUp(""),
    });
  }

  return (
    <div className="lead-filter-bar">
      <div className="lead-filter-row">
        <div className="toolbar-search">
          <span className="toolbar-search-icon" aria-hidden>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" />
            </svg>
          </span>
          <input
            type="search"
            placeholder="Search name, email, phone, company…"
            value={searchInput}
            onChange={(e) => onSearchInputChange(e.target.value)}
          />
        </div>

        <button
          type="button"
          className={`btn btn-secondary btn-sm filter-trigger${
            drawerOpen ? " is-open" : ""
          }`}
          onClick={() => setDrawerOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={drawerOpen}
        >
          <svg
            className="filter-trigger-icon"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <path d="M2 3h12" />
            <path d="M4 8h8" />
            <path d="M6 13h4" />
          </svg>
          Filters
          {activeCount > 0 ? (
            <span className="filter-trigger-count">{activeCount}</span>
          ) : null}
        </button>

        {activeCount > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="toolbar-clear"
            onClick={() => clear()}
          >
            Clear all
          </Button>
        ) : null}
      </div>

      {chips.length > 0 ? (
        <div className="filter-chips" role="list">
          {chips.map((c) => (
            <span key={c.key} className="filter-chip" role="listitem">
              <span className="filter-chip-label">{c.label}</span>
              <span className="filter-chip-value">{c.value}</span>
              <button
                type="button"
                className="filter-chip-remove"
                onClick={c.onRemove}
                aria-label={`Remove ${c.label} filter`}
              >
                <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M3 3l6 6" />
                  <path d="M9 3l-6 6" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <p className="filter-sort-summary">
        <span className="filter-sort-summary-label">Sorted by</span>
        <span className="filter-sort-summary-value">
          {sortSummaryLabel(filters.sort, isSales)}
        </span>
      </p>

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeCount={activeCount}
        onReset={() => clear()}
      >
        <FilterField label="Status">
          <PillSelect
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={setStatus}
          />
        </FilterField>

        <FilterField label="Source">
          <PillSelect
            value={filters.source}
            options={SOURCE_OPTIONS}
            onChange={setSource}
          />
        </FilterField>

        <FilterField label="Assignee">
          <Combobox
            value={filters.assignee}
            options={memberOptions}
            onChange={setAssignee}
            placeholder={
              membersIndex.loading ? "Loading…" : "Any assignee"
            }
            allLabel="Any assignee"
            emptyLabel="No matching members"
            searchPlaceholder="Search members…"
          />
        </FilterField>

        <FilterField label="Campaign">
          <Combobox
            value={filters.campaign}
            options={campaignOptions}
            onChange={setCampaign}
            placeholder="Any campaign"
            allLabel="Any campaign"
            emptyLabel={
              campaignOptions.length === 0
                ? "No campaigns yet"
                : "No matching campaigns"
            }
            searchPlaceholder="Search campaigns…"
          />
        </FilterField>

        <FilterField label="City">
          <MultiCombobox
            values={filters.city}
            options={cityOptions}
            onChange={setCity}
            placeholder="Any city"
            clearLabel="Any city"
            emptyLabel={
              cityOptions.length === 0 ? "No cities yet" : "No matching cities"
            }
            searchPlaceholder="Search cities…"
          />
        </FilterField>

        <FilterField label="State">
          <MultiCombobox
            values={filters.state}
            options={stateOptions}
            onChange={setState}
            placeholder="Any state"
            clearLabel="Any state"
            emptyLabel={
              stateOptions.length === 0 ? "No states yet" : "No matching states"
            }
            searchPlaceholder="Search states…"
          />
        </FilterField>

        <FilterField label="Date received">
          <div className="filter-date-row">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date"
            />
            <span aria-hidden>→</span>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date"
            />
          </div>
        </FilterField>

        <FilterField label="Follow-up">
          <PillSelect
            value={filters.followUp}
            options={FOLLOW_UP_OPTIONS}
            onChange={setFollowUp}
          />
        </FilterField>

        <FilterField label="Sort by">
          <PillSelect
            value={filters.sort}
            options={SORT_OPTIONS}
            onChange={setSort}
          />
        </FilterField>
      </FilterDrawer>
    </div>
  );
}

function FilterDrawer({
  open,
  onClose,
  activeCount,
  onReset,
  children,
}: {
  open: boolean;
  onClose: () => void;
  activeCount: number;
  onReset: () => void;
  children: React.ReactNode;
}) {
  // Keep the drawer node mounted briefly after close so the transition runs;
  // for now we only mount when open and rely on the transition-from-translated
  // state since the backdrop+panel still animate via CSS on entry.
  if (!open) return null;
  return (
    <div className="filter-drawer-root" role="dialog" aria-modal="true" aria-label="Filter leads">
      <div className="filter-drawer-backdrop" onClick={onClose} />
      <aside className="filter-drawer-panel">
        <header className="filter-drawer-header">
          <div>
            <h3 className="filter-drawer-title">Filters</h3>
            <p className="filter-drawer-subtitle">
              {activeCount === 0
                ? "Narrow the lead list"
                : `${activeCount} active`}
            </p>
          </div>
          <button
            type="button"
            className="filter-drawer-close"
            onClick={onClose}
            aria-label="Close filters"
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 4l8 8" />
              <path d="M12 4l-8 8" />
            </svg>
          </button>
        </header>

        <div className="filter-drawer-body">{children}</div>

        <footer className="filter-drawer-footer">
          <Button
            variant="ghost"
            size="sm"
            onClick={onReset}
            disabled={activeCount === 0}
          >
            Reset all
          </Button>
          <Button variant="primary" size="sm" onClick={onClose}>
            Done
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="filter-field">
      <div className="filter-field-label">{label}</div>
      <div className="filter-field-body">{children}</div>
    </div>
  );
}

function PillSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  return (
    <div className="filter-pill-group" role="radiogroup">
      <button
        type="button"
        role="radio"
        aria-checked={value === ""}
        className={`filter-pill${value === "" ? " is-active" : ""}`}
        onClick={() => onChange("")}
      >
        Any
      </button>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`filter-pill${value === o.value ? " is-active" : ""}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Combobox({
  value,
  options,
  onChange,
  placeholder,
  allLabel,
  emptyLabel,
  searchPlaceholder,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  placeholder: string;
  allLabel: string;
  emptyLabel: string;
  searchPlaceholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  // Reset query whenever the menu closes so reopening starts clean.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const selectedLabel = useMemo(() => {
    if (!value) return null;
    const match = options.find((o) => o.value === value);
    return match?.label ?? value;
  }, [value, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 100);
    return options
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 100);
  }, [query, options]);

  return (
    <div className={`combobox${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="combobox-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`combobox-value${selectedLabel ? "" : " is-placeholder"}`}
          title={selectedLabel ?? undefined}
        >
          {selectedLabel ?? placeholder}
        </span>
        <svg
          className="combobox-caret"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open ? (
        <div className="combobox-menu" role="listbox">
          <div className="combobox-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="combobox-list">
            <button
              type="button"
              role="option"
              aria-selected={value === ""}
              className={`combobox-item${value === "" ? " is-active" : ""}`}
              onClick={() => {
                onChange("");
                setOpen(false);
              }}
            >
              <span className="combobox-item-label muted">{allLabel}</span>
            </button>
            {filtered.length === 0 ? (
              <div className="combobox-empty">{emptyLabel}</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  aria-selected={value === o.value}
                  className={`combobox-item${
                    value === o.value ? " is-active" : ""
                  }`}
                  onClick={() => {
                    onChange(value === o.value ? "" : o.value);
                    setOpen(false);
                  }}
                  title={o.label}
                >
                  <span className="combobox-item-label">{o.label}</span>
                  {value === o.value ? (
                    <svg
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden
                    >
                      <path d="M3 8.5l3 3 7-7" />
                    </svg>
                  ) : null}
                </button>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MultiCombobox({
  values,
  options,
  onChange,
  placeholder,
  clearLabel,
  emptyLabel,
  searchPlaceholder,
}: {
  values: string[];
  options: Array<{ value: string; label: string }>;
  onChange: (v: string[]) => void;
  placeholder: string;
  clearLabel: string;
  emptyLabel: string;
  searchPlaceholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  // Reset query whenever the menu closes so reopening starts clean.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const selected = useMemo(() => new Set(values), [values]);

  const triggerLabel = useMemo(() => {
    if (values.length === 0) return null;
    const first =
      options.find((o) => o.value === values[0])?.label ?? values[0];
    return values.length === 1 ? first : `${first} +${values.length - 1}`;
  }, [values, options]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options.slice(0, 100);
    return options
      .filter((o) => o.label.toLowerCase().includes(q))
      .slice(0, 100);
  }, [query, options]);

  const toggle = (value: string) => {
    if (selected.has(value)) {
      onChange(values.filter((v) => v !== value));
    } else {
      onChange([...values, value]);
    }
  };

  return (
    <div className={`combobox${open ? " is-open" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="combobox-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          className={`combobox-value${triggerLabel ? "" : " is-placeholder"}`}
          title={values.length ? values.join(", ") : undefined}
        >
          {triggerLabel ?? placeholder}
        </span>
        <svg
          className="combobox-caret"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 6l4 4 4-4" />
        </svg>
      </button>

      {open ? (
        <div className="combobox-menu" role="listbox" aria-multiselectable="true">
          <div className="combobox-search">
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="7" cy="7" r="5" />
              <path d="M11 11l3.5 3.5" />
            </svg>
            <input
              ref={inputRef}
              type="search"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="combobox-list">
            <button
              type="button"
              role="option"
              aria-selected={values.length === 0}
              className={`combobox-item${values.length === 0 ? " is-active" : ""}`}
              onClick={() => onChange([])}
            >
              <span className="combobox-item-label muted">{clearLabel}</span>
            </button>
            {filtered.length === 0 ? (
              <div className="combobox-empty">{emptyLabel}</div>
            ) : (
              filtered.map((o) => {
                const isOn = selected.has(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    role="option"
                    aria-selected={isOn}
                    className={`combobox-item${isOn ? " is-active" : ""}`}
                    onClick={() => toggle(o.value)}
                    title={o.label}
                  >
                    <span className="combobox-item-label">{o.label}</span>
                    {isOn ? (
                      <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden
                      >
                        <path d="M3 8.5l3 3 7-7" />
                      </svg>
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
